// lib/leasing/sourceRentRoll.ts
import { UnitType, type SourceRentRollField } from '#types/types';

// The same finite field matrix drives table columns and labelled edit controls.
export const SOURCE_RENT_ROLL_FIELDS: readonly SourceRentRollField[] = [
  { entity: 'Unit', key: 'unitIdentifier', label: 'Unit', group: 'Unit', kind: 'text' },
  { entity: 'Unit', key: 'name', label: 'Unit name', group: 'Unit', kind: 'text' },
  { entity: 'Unit', key: 'status', label: 'Occupancy status', group: 'Unit', kind: 'readonly' },
  { entity: 'Unit', key: 'type', label: 'Unit type', group: 'Unit', kind: 'unitType' },
  { entity: 'Unit', key: 'areaSqm', label: 'Area (m²)', group: 'Unit', kind: 'number' },
  { entity: 'Unit', key: 'areaSqft', label: 'Area (sq ft)', group: 'Unit', kind: 'readonly' },
  { entity: 'Unit', key: 'frontage', label: 'Frontage (m)', group: 'Unit', kind: 'number' },
  { entity: 'Unit', key: 'askingRent', label: 'Asking rent', group: 'Unit', kind: 'number' },
  { entity: 'Unit', key: 'askingRentCurrency', label: 'Asking currency', group: 'Unit', kind: 'text' },
  { entity: 'Unit', key: 'description', label: 'Description', group: 'Unit', kind: 'text' },
  { entity: 'Deal', key: 'tenantLegalName', label: 'Tenant legal name', group: 'Lease', kind: 'text' },
  { entity: 'Deal', key: 'status', label: 'Lease status', group: 'Lease', kind: 'readonly' },
  { entity: 'Deal', key: 'permittedUse', label: 'Permitted use', group: 'Lease', kind: 'text' },
  { entity: 'Deal', key: 'rentAmount', label: 'Contractual rent', group: 'Rent', kind: 'number' },
  { entity: 'Deal', key: 'rentCurrency', label: 'Rent currency', group: 'Rent', kind: 'text' },
  { entity: 'Deal', key: 'rentBasis', label: 'Rent basis', group: 'Rent', kind: 'text' },
  { entity: 'Deal', key: 'rentPaymentFrequency', label: 'Payment frequency', group: 'Rent', kind: 'text' },
  { entity: 'Deal', key: 'rentPaymentTiming', label: 'Payment timing', group: 'Rent', kind: 'text' },
  { entity: 'Deal', key: 'rentIndexationType', label: 'Indexation', group: 'Rent', kind: 'text' },
  { entity: 'Deal', key: 'rentVariablePercent', label: 'Variable rent (%)', group: 'Rent', kind: 'number' },
  { entity: 'Deal', key: 'opexCamAmount', label: 'Service charges', group: 'Charges', kind: 'number' },
  { entity: 'Deal', key: 'opexTaxesAmount', label: 'Taxes', group: 'Charges', kind: 'number' },
  { entity: 'Deal', key: 'opexMarketingFeeAmount', label: 'Marketing fee', group: 'Charges', kind: 'number' },
  { entity: 'Deal', key: 'opexBasis', label: 'Charges basis', group: 'Charges', kind: 'text' },
  { entity: 'Deal', key: 'securityDepositAmount', label: 'Security deposit', group: 'Guarantees', kind: 'number' },
  { entity: 'Deal', key: 'securityDepositMonths', label: 'Deposit (months)', group: 'Guarantees', kind: 'number' },
  { entity: 'Deal', key: 'securityGapGuaranteeMonths', label: 'Guarantee (months)', group: 'Guarantees', kind: 'number' },
  { entity: 'Deal', key: 'leaseStartDate', label: 'Lease start', group: 'Dates and terms', kind: 'date' },
  { entity: 'Deal', key: 'leaseEndDate', label: 'Lease expiry', group: 'Dates and terms', kind: 'date' },
  { entity: 'Deal', key: 'leaseDurationYears', label: 'Duration (years)', group: 'Dates and terms', kind: 'number' },
  { entity: 'Deal', key: 'leaseFirmPeriodYears', label: 'Firm period (years)', group: 'Dates and terms', kind: 'number' },
  { entity: 'Deal', key: 'leaseBreakOptions', label: 'Break options', group: 'Dates and terms', kind: 'text' },
  { entity: 'Deal', key: 'possessionDate', label: 'Possession date', group: 'Dates and terms', kind: 'date' },
  { entity: 'Deal', key: 'openingDate', label: 'Opening date', group: 'Dates and terms', kind: 'date' },
  { entity: 'Deal', key: 'rentCommencementDate', label: 'Rent commencement', group: 'Dates and terms', kind: 'date' },
  { entity: 'Deal', key: 'proposalValidityDate', label: 'Proposal valid until', group: 'Dates and terms', kind: 'date' },
  { entity: 'Deal', key: 'contactName', label: 'Contact name', group: 'Contact', kind: 'text' },
  { entity: 'Deal', key: 'contactCompany', label: 'Contact company', group: 'Contact', kind: 'text' },
  { entity: 'Deal', key: 'contactEmail', label: 'Contact email', group: 'Contact', kind: 'text' },
  { entity: 'Deal', key: 'contactPhone', label: 'Contact phone', group: 'Contact', kind: 'text' },
  { entity: 'Deal', key: 'contactNotes', label: 'Contact notes', group: 'Contact', kind: 'text' },
  { entity: 'Deal', key: 'incentiveRentFreeMonths', label: 'Rent-free (months)', group: 'Incentives', kind: 'number' },
  { entity: 'Deal', key: 'incentiveFitOutAmount', label: 'Landlord fit-out', group: 'Incentives', kind: 'number' },
  { entity: 'Deal', key: 'incentiveFitOutAmountPerSqm', label: 'Landlord fit-out / m²', group: 'Incentives', kind: 'number' },
  { entity: 'Deal', key: 'estimatedTenantFitoutCost', label: 'Tenant fit-out estimate', group: 'Incentives', kind: 'number' },
  { entity: 'Deal', key: 'estimatedTenantFitoutCostPerSqm', label: 'Tenant fit-out estimate / m²', group: 'Incentives', kind: 'number' },
  { entity: 'Deal', key: 'incentiveHasStepRents', label: 'Stepped rent', group: 'Incentives', kind: 'boolean' },
];

export function rentRollValue(value: unknown, kind?: SourceRentRollField['kind']): string {
  if (value == null || value === '') return '';
  if (kind === 'date') return String(value).slice(0, 10);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function rentRollPayload(entity: 'Unit' | 'Deal', values: Record<string, string>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => {
    const field = SOURCE_RENT_ROLL_FIELDS.find(f => f.entity === entity && f.key === key && f.kind !== 'readonly');
    if (!field) throw new Error(`Field ${key} is not editable.`);
    if (value === '') {
      if (key === 'unitIdentifier' || field.kind === 'unitType') throw new Error(`${field.label} is required.`);
      return [key, null];
    }
    if (field.kind === 'number') {
      const number = Number(value);
      if (!value.trim() || !Number.isFinite(number)) throw new Error(`${field.label} must be a number.`);
      return [key, number];
    }
    if (field.kind === 'date') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value) throw new Error(`${field.label} must be a date.`);
      return [key, new Date(`${value}T00:00:00.000Z`).toISOString()];
    }
    if (field.kind === 'boolean') {
      if (value !== 'true' && value !== 'false') throw new Error(`${field.label} must be Yes or No.`);
      return [key, value === 'true'];
    }
    if (field.kind === 'unitType' && !Object.hasOwn(UnitType, value)) throw new Error('Choose a valid unit type.');
    return [key, value];
  }));
}
